import ABCDReduction from './ABCDReduction';
import BaseDataObject from './BaseDataObject';
import BaseHorseReductionSet from './BaseHorseReductionSet';
import BetConfig from './BetConfig';
import BetKey from './BetKey';
import DividendReduction from './DividendReduction';
import FactorReduction from './FactorReduction';
import JockeyReduction from './JockeyReduction';
import OwnReductionSet from './OwnReductionSet';
import PointReductionSet from './PointReductionSet';
import StartNumberReduction from './StartNumberReduction';
import TrainerReduction from './TrainerReduction';
import TrendReductionSet from './TrendReductionSet';

const GALLOP_TRACKS = new Set([34, 41, 42, 45, 47, 48]);

// ---------------------------------------- BetInformation ----------------------------------------

export default class BetInformation extends BaseDataObject {
  getID() {
    return this.getInteger('id');
  }

  isEmpty() {
    return this.getNumberOfRows() === 0;
  }

  getBetKey() {
    return this.getObject('betKey', BetKey);
  }

  getBetConfig() {
    let v = this.getObject('betConfig', BetConfig);

    if (!v) {
      v = new BetConfig();
      v.setPublished(false);
      this.setObject('betConfig', v);
    }
    return v;
  }

  setBetConfig(v) {
    this.setObject('betConfig', v);
    this.getBetConfig().invalidate();
  }

  getRows() {
    return this.getObjects('rows', BetRow);
  }

  getRow(index) {
    let row = this.getObject('rows', BetRow, index);
    if (!row) {
      row = new BetRow();
      this.setObject('rows', row, index);
    }
    return row;
  }

  getNumberOfRows() {
    return this.getNumberOf('rows');
  }

  getReductionSet() {
    return this.getObject('reductionRequirements', BetReductionSet);
  }

  setPresetReduction(presetReduction) {
    this.data.reductionRequirements.presetReduction = presetReduction;
    return;
  }

  getPresetReduction() {
    const reductionSet = this.getReductionSet();
    return reductionSet && reductionSet.data.presetReduction;
  }

  // Array of the track id for each race. The values will be the same
  // for most bets and identical to betKey.trackKey but when it's a bet
  // played on two tracks (aka "saxade lopp") betKey.trackKey will contain
  // "40", a number that doesn't correlate to any real track.
  getTrackIds() {
    return this.getArray('trackIds');
  }

  hasInternationalTrack() {
    // See https://www.atg.se/banor/internationella-bankoder
    return this.getTrackIds().some(id => id >= 50);
  }

  isGallop() {
    return GALLOP_TRACKS.has(this.getBetKey().getTrackKey());
  }
}
// ---------------------------------------- BetRow ----------------------------------------

class BetReductionSet extends BaseDataObject {
  getReductionsMap() {
    return {
      startNumberReduction: StartNumberReduction,
      pointReductionSet: PointReductionSet,
      abcdReduction: ABCDReduction,
      jockeyReduction: JockeyReduction,
      trainerReduction: TrainerReduction,
      baseHorseReductionSet: BaseHorseReductionSet,
      dividendReduction: DividendReduction,
      factorReduction: FactorReduction,
      ownReductionSet: OwnReductionSet,
      trendReductionSet: TrendReductionSet,
    };
  }

  getReduction(name) {
    const map = this.getReductionsMap();
    const cls = map[name];
    if (cls) {
      let obj = this.getObject(name, cls);
      if (!obj) {
        obj = new cls();
        this.setObject(name, obj);
      }
      return obj;
    }
  }

  getReductions() {
    const map = this.getReductionsMap();
    const reductions = {};
    for (const name in map) {
      reductions[name] = this.getReduction(name, map[name]);
    }
    return reductions;
  }

  getStartNumberReduction() {
    return this.getReduction('startNumberReduction');
  }

  getPointReductionSet() {
    return this.getReduction('pointReductionSet');
  }

  getABCDReduction() {
    return this.getReduction('abcdReduction');
  }

  getJockeyReduction() {
    return this.getReduction('jockeyReduction');
  }

  getTrainerReduction() {
    return this.getReduction('trainerReduction');
  }

  getBaseHorseReductionSet() {
    return this.getReduction('baseHorseReductionSet');
  }

  getDividendReduction() {
    return this.getReduction('dividendReduction');
  }

  getFactorReduction() {
    return this.getReduction('factorReduction');
  }

  getOwnReduction() {
    return this.getReduction('ownReductionSet');
  }

  getTrendReduction() {
    return this.getReduction('trendReductionSet');
  }
}

// ---------------------------------------- BetRow ----------------------------------------

class BetRow extends BaseDataObject {
  getNumberOfHorses() {
    return this.getInteger('numberOfHorses');
  }

  setNumberOfHorses(v) {
    this.invalidate();
    return this.setInteger('numberOfHorses', v);
  }

  getPickedHorses() {
    return this.getOrder().slice(0, this.getNumberOfHorses());
  }

  getStartNumberAtPosition(position) {
    return this.data.order ? this.data.order[position] : position + 1;
  }

  setOrder(order) {
    this.setIntegerArray('order', order);
    this.invalidate();
  }

  getOrder() {
    return this.getIntegerArray('order');
  }

  changeOrder(indexMap) {
    const newOrder = [];
    for (const [toIndex, fromIndex] of indexMap.entries()) {
      newOrder[toIndex] = this.getStartNumberAtPosition(fromIndex);
    }
    this.data.order = newOrder;
    this.invalidate();
  }

  getReserveNumbers() {
    return this.data.reserveNumbers || [];
  }

  getReserveNumber(position) {
    const reserveNumbers = this.data.reserveNumbers || [];
    const reserveNumber = reserveNumbers[position] || 0;
    return reserveNumber;
  }

  getNumberOfReserves() {
    return this.data.reserveNumbers.length;
  }

  setReserveNumber(position, reserveNumber, numberOfStarts) {
    const reserveNumbers = this.data.reserveNumbers || [];
    const previousPosition = reserveNumbers.indexOf(reserveNumber);
    const previousReserveNumber = this.getReserveNumber(position);

    if (previousPosition === position) {
      reserveNumbers[position] = 0;
    } else {
      if (previousPosition !== -1) {
        reserveNumbers[previousPosition] = previousReserveNumber;
      }
      reserveNumbers[position] = reserveNumber;
    }

    let count = 0;
    for (let n = 0; n < reserveNumbers.length; n++) {
      if (reserveNumbers[n]) {
        count++;
      } else {
        reserveNumbers[n] = 0;
      }
    }

    if (count === 0) {
      const emptyReserveNumbers = [];
      for (let i = 0; i < numberOfStarts; i++) {
        emptyReserveNumbers.push(0);
      }
    } else {
      this.data.reserveNumbers = reserveNumbers;
    }
    this.invalidate();
  }

  getPointReductionPoints(position, reductionIndex) {
    if (this.data.pointReductions == null) {
      this.data.pointReductions = [];
    }

    if (this.data.pointReductions[reductionIndex] == null) {
      this.data.pointReductions[reductionIndex] = [];
    }

    if (this.data.pointReductions[reductionIndex][position] == null) {
      this.data.pointReductions[reductionIndex][position] = position + 1;
    }

    return Number.parseInt(
      this.data.pointReductions[reductionIndex][position],
      10,
    );
  }

  setPointReductionPoints(position, reductionIndex, value) {
    if (this.data.pointReductions == null) {
      this.data.pointReductions = [];
    }

    if (this.data.pointReductions[reductionIndex] == null) {
      this.data.pointReductions[reductionIndex] = [];
    }

    this.data.pointReductions[reductionIndex][position] = Number.parseInt(
      value,
      10,
    );
    this.invalidate();
  }

  deletePointReduction(index) {
    this.deleteAt('pointReductions', index);
    this.invalidate();
  }

  deleteTrendPercentage() {
    this.setObject('trendPercentage', []);
    this.invalidate();
  }

  getTrendPercentage(position) {
    if (this.data.trendPercentage == null) {
      this.data.trendPercentage = [];
    }

    return this.data.trendPercentage[position];
  }

  setTrendPercentage(position, value) {
    if (this.data.trendPercentage == null) {
      this.data.trendPercentage = [];
    }

    this.data.trendPercentage[position] = Number.parseFloat(value, 10);
    this.invalidate();
  }

  getOwnReductionPercentages() {
    if (this.data.ownReductions == null) {
      return [];
    }
    return this.data.ownReductions;
  }

  getOwnReductionPercentage(position) {
    if (this.data.ownReductions == null) {
      this.data.ownReductions = [];
    }

    return this.data.ownReductions[position];
  }

  setOwnReductionPercentage(position, value) {
    if (this.data.ownReductions == null) {
      this.data.ownReductions = [];
    }

    const parsed = Number.parseFloat(value, 10);
    this.data.ownReductions[position] =
      parsed > 10 ? Math.round(parsed) : Math.round(parsed * 10) / 10;
    this.invalidate();
  }

  deleteOwnReductionPercentage() {
    this.setObject('ownReductions', []);
    this.invalidate();
  }

  getABCDReductionLetter(position) {
    if (!this.data.abcdReduction) {
      this.data.abcdReduction = [];
    }

    if (!this.data.abcdReduction[position]) {
      const defaultLetters = ['A', 'B', 'B', 'C', 'C', 'C', 'D'];
      const i = Math.min(position, defaultLetters.length - 1);
      const letter = defaultLetters[i];
      this.data.abcdReduction[position] = letter;
    }

    return this.data.abcdReduction[position];
  }

  setABCDReductionLetter(position, value) {
    this.setObject('abcdReduction', value, position);
    this.invalidate();
  }

  deleteABCDReduction() {
    this.setObject('abcdReduction', []);
    this.invalidate();
  }

  hasAnyFactors() {
    if (!this.data.factorReduction) {
      return false;
    }

    const factors = this.data.factorReduction;
    for (const factor of factors) {
      if (factor > 1) {
        return true;
      }
    }
    return false;
  }

  getFactorReductionFactor(position) {
    if (this.data.factorReduction == null) {
      this.data.factorReduction = [];
    }
    if (this.data.factorReduction[position] == null) {
      this.data.factorReduction[position] = 1;
    }
    return this.data.factorReduction[position];
  }

  setFactorReductionFactor(position, value) {
    this.data.factorReduction[position] = value;
    this.invalidate();
  }

  deleteFactorReduction() {
    this.setObject('factorReduction', []);
    this.invalidate();
  }

  getBaseHorseReductionState(position, reductionIndex) {
    if (this.data.baseHorseReductions == null) {
      this.data.baseHorseReductions = [];
    }

    if (this.data.baseHorseReductions[reductionIndex] == null) {
      this.data.baseHorseReductions[reductionIndex] = [];
    }

    if (this.data.baseHorseReductions[reductionIndex][position] == null) {
      this.data.baseHorseReductions[reductionIndex][position] = false;
    }

    return this.data.baseHorseReductions[reductionIndex][position];
  }

  setBaseHorseReductionState(position, reductionIndex, value) {
    if (this.data.baseHorseReductions == null) {
      this.data.baseHorseReductions = [];
    }

    if (this.data.baseHorseReductions[reductionIndex] == null) {
      this.data.baseHorseReductions[reductionIndex] = [];
    }

    this.data.baseHorseReductions[reductionIndex][position] = value;
    this.invalidate();
  }

  deleteBaseHorseReduction(index) {
    this.deleteAt('baseHorseReductions', index);
    this.invalidate();
  }

  setCurrentTrend(currentTrend) {
    this.setFloatArray('trend', currentTrend);
    this.invalidate();
  }

  getCurrentTrend() {
    return this.getFloatArray('trend');
  }

  setLatestTrend(latestTrend) {
    this.setFloatArray('latestTrend', latestTrend);
    this.invalidate();
  }

  getLatestTrend() {
    return this.getFloatArray('latestTrend');
  }

  setPeriodTrend(oldestTrend) {
    this.setFloatArray('periodTrend', oldestTrend);
    this.invalidate();
  }

  getPeriodTrend() {
    return this.getFloatArray('periodTrend');
  }
}
